import { useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { EVENTS } from "@bash/shared";
import { useConnectionStore } from "@/stores/connectionStore";
import { useTaskStore } from "@/stores/taskStore";

// Debounce so mouse-scroll across a list of tasks doesn't fire 20 wakes.
// 150ms is short enough to not feel laggy but long enough to skip drive-bys.
const HOVER_DEBOUNCE_MS = 150;

// Don't re-preload the same task twice within this window — a hovered task's
// persisted messages don't change between hovers, so a re-sync is a wasted
// Supabase round trip.
const PREWARM_DEDUP_WINDOW_MS = 30_000;

// Module-scoped so it's shared across every instance of the hook — hovering
// a task card in one list and then the task nav should only preload once.
const recentlyPrewarmed = new Map<string, number>();

/**
 * Hover-preload for task cards.
 *
 * When the user hovers a task we fire ONE thing on their socket:
 *
 *   MESSAGE_SYNC_REQUEST — preloads the task's persisted messages into the
 *   Zustand store so the conversation paints in the same frame as navigation.
 *   Without this, switchTask emits the sync only after the route has mounted,
 *   and the user sees a 200-800ms blank gap between the URL changing and the
 *   messages appearing.
 *
 * We also router.prefetch(`/task/<id>`) — the task route is a public-cacheable
 * static shell (see task/[[...slug]]/page.tsx), so a never-opened id is generated
 * + edge-cached on first request. Doing that during hover turns the click into a
 * CDN cache hit instead of a ~1s cold on-demand generation.
 *
 * We deliberately do NOT wake the sandbox on hover. Hover is speculative — the
 * user skims past cards they never open — so waking would flash the connection
 * state ("Waking your sandbox") and spin up sandboxes for intent the user never
 * had. Sandbox warming is reserved for typing (SANDBOX_PRE_WARM in
 * task/layout.tsx), the only committed-intent signal.
 *
 * The preload is gated on cache state — if the store already has messages for
 * this task (live task, or a previously-hovered one within the dedup window),
 * we skip the redundant Supabase fetch.
 *
 * Usage:
 *   const { onHoverStart, onHoverEnd } = useTaskHoverPrewarm();
 *   <div onMouseEnter={() => onHoverStart(task.id)} onMouseLeave={onHoverEnd}>
 *
 * Safe to spam — debounced + de-duplicated.
 */
export function useTaskHoverPrewarm() {
  const router = useRouter();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onHoverStart = useCallback(
    (taskId: string | undefined | null) => {
      if (!taskId) return;

      // De-dup: if we prewarmed this taskId recently, skip.
      const last = recentlyPrewarmed.get(taskId);
      if (last && Date.now() - last < PREWARM_DEDUP_WINDOW_MS) return;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Warm the route's RSC payload so the click resolves from the client
        // router cache / edge CDN instead of a cold on-demand static generation.
        // Cheap + Next-deduped; needs no socket, so it runs before the sync gate.
        router.prefetch(`/task/${taskId}`);

        // Preload the task's persisted messages so the conversation paints
        // instantly on click. Skip when the store already holds messages for
        // this task — it's live (currently streaming) or recently hydrated, so a
        // re-sync would either no-op or risk clobbering optimistic state
        // (handleMessageSync's guards in syncHandlers.ts handle the latter, but
        // skipping here saves a Supabase round-trip on the server).
        const socket = useConnectionStore.getState().getSocket();
        const cachedTask = useTaskStore.getState().tasks.get(taskId);
        if (socket && (!cachedTask || cachedTask.messages.length === 0)) {
          socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
        }

        recentlyPrewarmed.set(taskId, Date.now());
      }, HOVER_DEBOUNCE_MS);
    },
    [router]
  );

  const onHoverEnd = useCallback(() => {
    // If the user moved off before the debounce fired, cancel — treat it as
    // a drive-by, not a real intent to open.
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { onHoverStart, onHoverEnd };
}
